// eslint-disable-next-line import/no-extraneous-dependencies
import cloneDeep from 'lodash/cloneDeep';
import format from 'date-fns/format';
import subDays from 'date-fns/subDays';
import { IDateRange } from '../../shared/DateRangePickerCustom/interface';
import {
  CUSTOM,
  DISPLAY_VALUE,
  LAST_30_DAYS,
  LAST_60_DAYS,
  LAST_7_DAYS,
  TODAY,
  YESTERDAY,
} from './constants';
import { ITimePeriod } from './interface';

const todaysDate = new Date();

const calculateDate = (date: Date, numberOfDays = 0) =>
  format(subDays(date, numberOfDays), 'dd/MM/yyyy');

const calculateFromToDate = (numberOfDaysFrom = 0, numberOfDaysTo = 0) => ({
  fromDate: calculateDate(todaysDate, numberOfDaysFrom),
  toDate: calculateDate(todaysDate, numberOfDaysTo),
});

const constructDisplayValue = (
  date: Date = new Date(),
  days = 0,
  showYear = false,
) => format(subDays(date, days), `dd MMM ${showYear ? 'yy' : ''}`);

const checkDisplayYear = (start = {}, end = {}) => {
  const { date: startDate = new Date(), dayDiff: startDayDiff = 0 } = {
    ...start,
  };
  const { date: endDate = new Date(), dayDiff: endDayDiff = 0 } = { ...end };
  const findYear = (date = new Date(), days = 0) =>
    format(subDays(date, days), 'yy');
  return findYear(startDate, startDayDiff) !== findYear(endDate, endDayDiff);
};

const constructStartEndDisplay = (start = {}, end = {}) => {
  const { date: startDate = new Date(), dayDiff: startDayDiff = 0 } = {
    ...start,
  };
  const { date: endDate = new Date(), dayDiff: endDayDiff = 0 } = { ...end };

  const isStartYearDisplay = checkDisplayYear(start, end);
  let displayValue = constructDisplayValue(
    startDate,
    startDayDiff,
    isStartYearDisplay,
  );
  if (endDate) {
    displayValue += ` - ${constructDisplayValue(endDate, endDayDiff, true)}`;
  }
  return displayValue;
};

const createDisplayValue = (
  { start = {}, end = {}, displayString = '' },
  isDelimiterRequired = true,
) =>
  displayString
    .concat(
      `${isDelimiterRequired ? ', ' : ' '}`,
      constructStartEndDisplay(start, end),
    )
    .trim();

const DEFAULT_CUSTOM_TIME_PERIOD = {
  timePeriodId: CUSTOM,
  displayValue: DISPLAY_VALUE.CUSTOM,
  fromDate: '',
  toDate: '',
  isSelected: false,
};

export const TIME_PERIOD_LIST = [
  {
    timePeriodId: TODAY,
    displayValue: createDisplayValue({
      start: {
        date: todaysDate,
      },
      displayString: DISPLAY_VALUE.TODAY,
    }),
    ...calculateFromToDate(),
    isSelected: false,
  },
  {
    timePeriodId: YESTERDAY,
    displayValue: createDisplayValue({
      start: {
        date: todaysDate,
        dayDiff: 1,
      },
      displayString: DISPLAY_VALUE.YESTERDAY,
    }),
    ...calculateFromToDate(1, 1),
    isSelected: false,
  },
  {
    timePeriodId: LAST_7_DAYS,
    displayValue: createDisplayValue({
      start: { date: todaysDate },
      end: { date: todaysDate, dayDiff: 6 },
      displayString: DISPLAY_VALUE.LAST_7_DAYS,
    }),
    ...calculateFromToDate(6, 0),
    isSelected: true,
  },
  {
    timePeriodId: LAST_30_DAYS,
    displayValue: createDisplayValue({
      start: { date: todaysDate },
      end: { date: todaysDate, dayDiff: 29 },
      displayString: DISPLAY_VALUE.LAST_30_DAYS,
    }),
    ...calculateFromToDate(29, 0),
    isSelected: false,
  },
  {
    timePeriodId: LAST_60_DAYS,
    displayValue: createDisplayValue({
      start: { date: todaysDate },
      end: { date: todaysDate, dayDiff: 59 },
      displayString: DISPLAY_VALUE.LAST_60_DAYS,
    }),
    ...calculateFromToDate(59, 0),
    isSelected: false,
  },
  {
    ...DEFAULT_CUSTOM_TIME_PERIOD,
  },
];

export const updateTimePeriodList = (
  timePeriodArray: ITimePeriod[] = [],
  selectedTimePeriodId: string,
) =>
  cloneDeep(timePeriodArray).map((item: ITimePeriod) => {
    if (item.timePeriodId === CUSTOM) {
      return { ...DEFAULT_CUSTOM_TIME_PERIOD };
    }
    const currentItem = item;
    currentItem.isSelected = selectedTimePeriodId === item.timePeriodId;
    return currentItem;
  });

export const customTimePeriodModifier = (
  timePeriodList: ITimePeriod[] = [],
  date: IDateRange | undefined,
) => {
  const toDate = date?.to || new Date();
  const fromDate = date?.from || new Date();
  return cloneDeep(timePeriodList).map((item: ITimePeriod) => {
    if (item.timePeriodId === CUSTOM) {
      return {
        ...item,
        displayValue: createDisplayValue(
          {
            start: { date: toDate || fromDate },
            end: { date: toDate ? fromDate : '' },
            // displayString: DISPLAY_VALUE.CUSTOM,
          },
          false,
        ),
        fromDate: calculateDate(fromDate),
        toDate: calculateDate(toDate || fromDate),
        isSelected: true,
        ...date,
      };
    }
    return { ...item, isSelected: false };
  });
};

export const DEFAULT_SELECTED: IDateRange = {
  from: undefined,
  to: undefined,
};

export const getSelectedTimePeriod = (timePeriodList: ITimePeriod[] = []) =>
  timePeriodList.find(({ isSelected = false }) => Boolean(isSelected));
